/**
 * @module experience/one-time-payment
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */

import { InStoreCardDetails } from '@support/payment/instore-payment/models/instore-card-details.model';
import { Address } from '@experience/store-operations/interfaces/store-ops.interface';
import { PaymentMethodAddress } from '@support/api/proxies/requests/api-372.interface';
import { Expiration, PaymentMethod } from '@support/api/proxies/requests/api-2150.interface';
import { SHARED_PAYMENT } from '@support/payment/shared-payment/constants/shared-payment.constants';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import {
  Cart,
  Payment,
  SpecificationValue,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

export class PostOrderPayment {
  /**
   * To store cardId
   */
  public cartId: string;
  /**
   * To store orderId
   */
  public orderId: string;
  /**
   * To store actionCode
   */
  public actionCode: string;
  /**
   * To store transactionType
   */
  public transactionType: string;
  /**
   * To store orderType
   */
  public orderType: string;
  /**
   * To store payment method details
   */
  public paymentMethod: PaymentMethod;
  public bypassInventoryCheck: boolean;
  public bypassFraudCheck: boolean;
  public posId: string;
  /**
   * terms and condition
   */
  private termsAndConditionsAccepted: boolean;
  /**
   * terms and condition
   */
  private isStoreDigital: boolean;

  private orderLocation: object;

  constructor(
    cardData: InStoreCardDetails,
    cartId: string,
    transactionType: string,
    macAddress: string,
    storeAddress: Address,
    isStoreDigital: boolean = false,
    isSubmit?: boolean,
    objectService?: ObjectService,
    cart?: Cart,
    amount?: string,
    private commonUtilService?: CommonUtilService
  ) {
    this.getInitDetails(cartId, transactionType, isStoreDigital, isSubmit);
    const paymentMethodCode =
      cardData && cardData.paymentMethodCode ? cardData.paymentMethodCode : '';
    if (paymentMethodCode) {
      this.paymentMethod = this.createPaymentMethod(cardData, macAddress, storeAddress, cart);
    } else {
      this.paymentMethod = {};
    }
    if (transactionType === SHARED_PAYMENT.RETURN) {
      this.bypassFraudCheck = false;
      this.bypassInventoryCheck = false;
      this.posId = macAddress;
      const payment = this.getPayment(objectService.get(cart, 'payment'), objectService, amount);
      this.paymentMethod.authorizationId = objectService.get(payment, 'receivedPaymentId');
      this.paymentMethod.refundSequenceNo = objectService.get(
        this.getRefundSequenceNo(
          objectService.get(
            payment,
            'specificationGroup.0.SpecificationGroupItem.0.specificationValue',
            []
          )
        ),
        'text'
      );
      this.paymentMethod.transactionType = SHARED_PAYMENT.REFUND;
    }
    if (cardData && cardData.authorizationId) {
      this.paymentMethod.authorizationId = cardData.authorizationId;
    } else {
      this.orderLocation = {
        '@id': '',
        location: {
          '@id': '2',
        },
      };
    }
    this.termsAndConditionsAccepted = true;
    this.actionCode =
      cardData && cardData.signature ? SHARED_PAYMENT.SIGNATURE_UPDATE : this.actionCode;
  }

  /**
   * initializing instore details
   */
  public getInitDetails(
    cartId: string,
    transactionType: string,
    isStoreDigital: boolean = false,
    isSubmit?: boolean
  ): void {
    this.cartId = cartId;
    this.orderId = cartId;
    this.isStoreDigital = isStoreDigital;
    this.actionCode = isSubmit ? SHARED_PAYMENT.SUBMIT : SHARED_PAYMENT.ADD;
    this.transactionType = transactionType;
    this.orderType = transactionType;
  }

  /**
   * create payment method
   * @param {InStoreCardDetails} cardData
   * @param {string} macAddress
   * @param {Address} storeAddress
   * @returns {PaymentMethod}
   */
  public createPaymentMethod(
    cardData: InStoreCardDetails,
    macAddress?: string,
    storeAddress?: Address,
    cart?: Cart
  ): PaymentMethod {
    const paymentMethod: PaymentMethod = {};
    if (cardData) {
      paymentMethod.cardReadSource = cardData.cardReadSource;
      paymentMethod.posId = macAddress;
      paymentMethod.paymentId = this.commonUtilService?.isNumeric(cardData.paymentId)
        ? Math.floor(Number(cardData.paymentId)).toString()
        : String(cardData.paymentId);
      paymentMethod.source = SHARED_PAYMENT.JPAY;
      paymentMethod.saveForFuture = false;
      paymentMethod.storePaymentMethodIndicator = false;
      paymentMethod.transactionType = SHARED_PAYMENT.PAYMENT;
      paymentMethod.nameOnCard = cardData.nameOnCard;
      paymentMethod.cardNumber = cardData.cardNumber;
      paymentMethod.expiration = this.setExpiration(cardData);
      paymentMethod.address = {};
      paymentMethod.cardPresentIndicator = true;
      paymentMethod.terminalEntryCapability = cardData.terminalEntryCapability;
      paymentMethod.terminalType = cardData.terminalType;
      paymentMethod.terminalEntryMode = cardData.terminalEntryMode;
      paymentMethod.signature = cardData.signature;
      paymentMethod.electronicAuthenticationCapability =
        cardData.electronicAuthenticationCapability || '1';
      if (cardData.token && cardData.token.encryptionTarget !== '') {
        paymentMethod.token = cardData.token;
      }
      if (cardData.typeCode) {
        paymentMethod.typeCode = cardData.typeCode;
      }
      paymentMethod.cardTypeIndicator = cardData.cardTypeIndicator || '3';
      paymentMethod.method = cardData.paymentMethodCode;
      paymentMethod.paymentMethodCode = cardData.paymentMethodCode;
      paymentMethod.pin = this.getPin(cardData.PIN);
      paymentMethod.chargeAccountNumberIndicator = cardData.chargeAccountNumberIndicator || null;
      paymentMethod.integratedChipData = cardData.de55;
      paymentMethod.p2peIndicator = cardData.isEncryptionOn;
      paymentMethod.chipApplicationId = cardData.aid;
      paymentMethod.verificationMethod = cardData.cvm;
      paymentMethod.applicationCryptogram = cardData.applicationCryptogram;
      paymentMethod.chipApplicationName = cardData.applicationName;
      paymentMethod.category = cardData.paymentMethodCode;
      paymentMethod.onlineOfflineCode = SHARED_PAYMENT.ONLINE_OFFLINE_CODE;
    }
    return paymentMethod;
  }

  /**
   * get the pin Value
   * @param {string} pinvalue
   */
  private getPin(pinData: string): string {
    return pinData === 'null' ? '' : pinData;
  }

  /**
   * Set the payment method
   * @param {string} key
   * @param {string | boolean | number} value
   */
  public setPaymentMethodValue(key: string, value: object | string | boolean | number): void {
    this.paymentMethod[key] = value;
  }

  /**
   * Function to set Expiration
   * @param {InStoreCardDetails} cardDetails
   * @returns {Expiration}
   */
  private setExpiration(cardDetails: InStoreCardDetails): Expiration {
    const month = '0' + cardDetails.expirationMonth;
    const year = cardDetails.expirationYear || '';
    const last2 = 2;
    const expirationDetails: Expiration = {
      month: month.substring(month.length - last2),
      year: year.substring(year.length - last2),
    };
    return expirationDetails;
  }

  /**
   * Set Address to PaymentMethod
   * @param {Address} storeAddress
   */
  private setAddress(storeAddress: Address): PaymentMethodAddress {
    const zipCode = storeAddress.zip ? storeAddress.zip.split('-') : [];
    return {
      addressLine1: storeAddress.line1,
      addressLine2: storeAddress.line2,
      city: storeAddress.city,
      state: storeAddress.state,
      zipCode: zipCode[0],
    };
  }

  private getPayment(payments: Payment[], objectService?: ObjectService, amount?: string): Payment {
    const result = payments.filter((payment: Payment) => {
      return (
        Number(objectService.get(payment, 'authorizationAmount.text', '0.00')) === Number(amount)
      );
    });
    return objectService.get(result, '0');
  }

  private getRefundSequenceNo(specificationValue?: SpecificationValue[]): SpecificationValue {
    return specificationValue.find((specification: SpecificationValue) => {
      return specification.name === 'refundSequenceNo';
    });
  }
}
